// src/components/Home.js
import React, { useEffect, useMemo, useState } from "react";
import { Statistic, Spin, Card, Row, Col, Tabs } from "antd";
import { motion } from "framer-motion";
import { useStats } from "../contexts/statsContext";
import { useNavigate } from "react-router-dom";
import {
  BarChartOutlined,
  UserAddOutlined,
  CheckCircleOutlined,
  ShopOutlined,
  ArrowRightOutlined,
  ShoppingOutlined,
  SettingOutlined
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { TabPane } = Tabs;

const Home = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useStats();
  const { stats, loading } = state;

  // ⬇️ local state for production status counts
  const [prodCounts, setProdCounts] = useState({
    in_progress: 0,
    complete: 0,
    shipped: 0,
  });

  useEffect(() => {
    if (loading) {
      const fetchStats = async () => {
        try {
          const { data } = await axiosInstance.get("/admin/stats");
          if (data.success) {
            dispatch({
              type: "SET_STATS",
              payload: {
                pending_users: data.pending_users,
                registered_users: data.registered_users,
                available_products: data.available_products,
                total_products: data.total_products,
                today_orders: data.today_orders || 0,
                month_orders: data.month_orders || 0,
                awaiting_delivery: data.awaiting_delivery || 0,
                in_progress: data.in_progress || 0,
                awaiting_dispatch: data.awaiting_dispatch || 0,
              },
            });
          }
        } catch (error) {
          console.error("Error fetching stats:", error);
        }
      };

      // ⬇️ fetch production list once and compute breakdown
      const fetchProductionBreakdown = async () => {
        try {
          const { data } = await axiosInstance.get("/admin/production/list");
          const orders = Array.isArray(data?.orders) ? data.orders : [];
          const tally = { in_progress: 0, complete: 0, shipped: 0 };

          for (const o of orders) {
            const s = String(o.sheetProductionStatus || o.productionStatus || "").toLowerCase();
            if (s === "in progress") tally.in_progress += 1;
            else if (s === "complete") tally.complete += 1;
            else if (s === "shipped") tally.shipped += 1;
          }
          setProdCounts(tally);
        } catch (e) {
          // fall back: keep previous
          setProdCounts((p) => p);
        }
      };

      fetchStats();
      fetchProductionBreakdown();
    }
  }, [dispatch, loading]);

  // ⬇️ FIX: compute before any early return so hooks are never conditional
  const outOfStock = useMemo(() => {
    const total = Number(stats?.total_products || 0);
    const available = Number(stats?.available_products || 0);
    const calc = total - available;
    return calc > 0 ? calc : 0;
  }, [stats?.total_products, stats?.available_products]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1, y: 0, scale: 1,
      transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-4 text-gray-500 font-medium">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  const StatCard = ({ stat }) => (
    <motion.div
      variants={cardVariants}
      whileHover={{ y: -8, transition: { duration: 0.3 } }}
      className="h-full"
    >
      <Card
        className={`
          h-full cursor-pointer transition-all duration-300
          border-l-4 ${stat.borderColor}
          hover:shadow-lg hover:border-gray-300
          bg-white rounded-xl overflow-hidden stat-card
        `}
        bodyStyle={{ padding: '24px' }}
        onClick={() => navigate(stat.link, { state: stat.navState })}
      >
        <div className="stat-card-content h-full flex flex-col justify-between">
          <div>
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 rounded-xl bg-gradient-to-r ${stat.color} shadow-lg`}>
                <div className="text-white flex items-center justify-center">
                  {stat.icon}
                </div>
              </div>

              {stat.value > 0 && (
                <div className="flex flex-col items-end">
                  <div className="relative">
                    <div className="absolute inset-0 animate-ping opacity-75">
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${stat.color}`} />
                    </div>
                    <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${stat.color}`} />
                  </div>
                  <span className="text-xs text-green-500 font-medium mt-1">Active</span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-gray-500 text-sm font-medium mb-1">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stat.value?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-gray-400 mt-1">{stat.description}</p>
              </div>
            </div>
          </div>

          <div className="pt-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(stat.link, { state: stat.navState });
              }}
              className={`
                w-full py-2 px-4 rounded-lg text-sm font-medium
                bg-gradient-to-r ${stat.color} text-white
                transition-all duration-300 ease-in-out
                shadow-md hover:shadow-lg
                flex items-center justify-center space-x-2
                hover:scale-105
              `}
            >
              <span>View Details</span>
              <ArrowRightOutlined className="text-xs transform group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </Card>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard Overview</h1>
          <p className="text-gray-600">Monitor and manage your platform statistics and activities</p>
        </motion.div>

        {/* Tabs Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="mb-8"
        >
          <Card className="bg-white rounded-xl border-0 shadow-sm" bodyStyle={{ padding: '24px' }}>
            <Tabs defaultActiveKey="orders" type="line" size="large" className="custom-tabs equal-tabs">
              {/* 1) Orders Tab */}
              <TabPane
                tab={
                  <span className="flex items-center font-medium">
                    <ShoppingOutlined className="mr-2" />
                    Orders
                  </span>
                }
                key="orders"
              >
                <motion.div variants={containerVariants} initial="hidden" animate="visible">
                  <Row gutter={[24, 24]}>
                    <Col xs={24} sm={12} lg={8}>
                      <StatCard stat={{
                        title: "Today",
                        value: stats.today_orders,
                        icon: <ShoppingOutlined className="text-lg" />,
                        color: "from-blue-500 to-cyan-500",
                        borderColor: "border-l-blue-500",
                        link: "/all-orders",
                        description: "Today's orders",
                        navState: { ordersScope: "today" }
                      }} />
                    </Col>

                    <Col xs={24} sm={12} lg={8}>
                      <StatCard stat={{
                        title: "This Month",
                        value: stats.month_orders,
                        icon: <ShoppingOutlined className="text-lg" />,
                        color: "from-green-500 to-emerald-500",
                        borderColor: "border-l-green-500",
                        link: "/all-orders",
                        description: "Monthly orders",
                        navState: { ordersScope: "month" }
                      }} />
                    </Col>

                    <Col xs={24} sm={12} lg={8}>
                      <StatCard stat={{
                        title: "Awaiting Delivery",
                        value: stats.awaiting_delivery,
                        icon: <ShoppingOutlined className="text-lg" />,
                        color: "from-amber-500 to-orange-500",
                        borderColor: "border-l-amber-500",
                        link: "/all-orders",
                        description: "Pending delivery",
                        navState: { ordersScope: "awaiting_delivery" }
                      }} />
                    </Col>
                  </Row>
                </motion.div>
              </TabPane>

              {/* 2) Production Tab */}
              <TabPane
                tab={
                  <span className="flex items-center font-medium">
                    <SettingOutlined className="mr-2" />
                    Production
                  </span>
                }
                key="production"
              >
                <motion.div variants={containerVariants} initial="hidden" animate="visible">
                  <Row gutter={[24, 24]}>
                    <Col xs={24} sm={12} lg={8}>
                      <StatCard stat={{
                        title: "In Progress",
                        value: prodCounts.in_progress,
                        icon: <SettingOutlined className="text-lg" />,
                        color: "from-purple-500 to-violet-600",
                        borderColor: "border-l-purple-500",
                        link: "/production-orders",
                        description: "In production",
                        navState: { filterStatus: "in progress" }
                      }} />
                    </Col>

                    <Col xs={24} sm={12} lg={8}>
                      <StatCard stat={{
                        title: "Complete",
                        value: prodCounts.complete,
                        icon: <SettingOutlined className="text-lg" />,
                        color: "from-amber-500 to-orange-500",
                        borderColor: "border-l-amber-500",
                        link: "/production-orders",
                        description: "Completed orders",
                        navState: { filterStatus: "complete" }
                      }} />
                    </Col>

                    <Col xs={24} sm={12} lg={8}>
                      <StatCard stat={{
                        title: "Shipped",
                        value: prodCounts.shipped,
                        icon: <SettingOutlined className="text-lg" />,
                        color: "from-red-500 to-pink-500",
                        borderColor: "border-l-red-500",
                        link: "/production-orders",
                        description: "Shipped orders",
                        navState: { filterStatus: "shipped" }
                      }} />
                    </Col>
                  </Row>
                </motion.div>
              </TabPane>

              {/* 3) Products Tab */}
              <TabPane
                tab={
                  <span className="flex items-center font-medium">
                    <ShopOutlined className="mr-2" />
                    Products
                  </span>
                }
                key="products"
              >
                <motion.div variants={containerVariants} initial="hidden" animate="visible">
                  <Row gutter={[24, 24]}>
                    <Col xs={24} sm={12} lg={8}>
                      <StatCard stat={{
                        title: "Available Products",
                        value: stats.available_products,
                        icon: <CheckCircleOutlined className="text-lg" />,
                        color: "from-amber-500 to-orange-500",
                        borderColor: "border-l-amber-500",
                        link: "/products",
                        description: "In stock"
                      }} />
                    </Col>

                    {/* Out of Stock */}
                    <Col xs={24} sm={12} lg={8}>
                      <StatCard stat={{
                        title: "Out of Stock",
                        value: outOfStock,
                        icon: <ShopOutlined className="text-lg" />,
                        color: "from-red-500 to-pink-500",
                        borderColor: "border-l-red-500",
                        link: "/products",
                        description: "Currently unavailable",
                        navState: { stock: "out" }
                      }} />
                    </Col>

                    <Col xs={24} sm={12} lg={8}>
                      <StatCard stat={{
                        title: "Total Products",
                        value: stats.total_products,
                        icon: <ShopOutlined className="text-lg" />,
                        color: "from-purple-500 to-violet-600",
                        borderColor: "border-l-purple-500",
                        link: "/products",
                        description: "All products"
                      }} />
                    </Col>
                  </Row>
                </motion.div>
              </TabPane>

              {/* 4) Users Tab */}
              <TabPane
                tab={
                  <span className="flex items-center font-medium">
                    <UserAddOutlined className="mr-2" />
                    Users
                  </span>
                }
                key="users"
              >
                <motion.div variants={containerVariants} initial="hidden" animate="visible">
                  <Row gutter={[24, 24]}>
                    <Col xs={24} sm={12} lg={12}>
                      <StatCard stat={{
                        title: "Registered Users",
                        value: stats.registered_users,
                        icon: <UserAddOutlined className="text-lg" />,
                        color: "from-green-500 to-emerald-500",
                        borderColor: "border-l-green-500",
                        link: "/users",
                        description: "Total users"
                      }} />
                    </Col>

                    <Col xs={24} sm={12} lg={12}>
                      <StatCard stat={{
                        title: "Pending Approvals",
                        value: stats.pending_users,
                        icon: <BarChartOutlined className="text-lg" />,
                        color: "from-blue-500 to-cyan-500",
                        borderColor: "border-l-blue-500",
                        link: "/requests",
                        description: "Awaiting review"
                      }} />
                    </Col>
                  </Row>
                </motion.div>
              </TabPane>
            </Tabs>
          </Card>
        </motion.div>

        {/* Additional Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <Card className="bg-white rounded-xl border-0 shadow-sm" bodyStyle={{ padding: '32px' }}>
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Real-time Analytics Dashboard</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Comprehensive overview of your platform's performance metrics.
                All data is updated in real-time with secure access and instant synchronization.
              </p>

              <div className="flex flex-wrap justify-center gap-6 text-sm">
                <div className="flex items-center space-x-2 text-gray-500">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span>Live Data Stream</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-500">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span>Enterprise Security</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-500">
                  <div className="w-2 h-2 bg-purple-500 rounded-full" />
                  <span>Instant Updates</span>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* ⬇ equal width/height tabs only + equal stat card heights */}
      <style>{`
        .equal-tabs .ant-tabs-nav .ant-tabs-nav-list { display: flex; width: 100%; }
        .equal-tabs .ant-tabs-nav .ant-tabs-tab {
          flex: 1 1 0; justify-content: center; text-align: center;
          height: 48px; padding: 12px 16px; margin: 0 !important;
        }
        .equal-tabs .ant-tabs-tab .anticon { margin-right: 8px; }
        @media (max-width: 640px) {
          .equal-tabs .ant-tabs-nav .ant-tabs-nav-list { gap: 8px; flex-wrap: wrap; }
          .equal-tabs .ant-tabs-nav .ant-tabs-tab { flex: 1 1 100%; height: 44px; }
        }

        /* Uniform card heights */
        .stat-card { min-height: 220px; }
        .stat-card .ant-card-body { height: 100%; display: flex; flex-direction: column; }
        .stat-card-content { height: 100%; display: flex; flex-direction: column; justify-content: space-between; }
      `}</style>
    </div>
  );
};

export default Home;
